import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch, getToken, setToken, clearToken, isTokenExpired, API_BASE_URL, clientPlatform } from '../dppApi';
import '../App.css';

const bloomMark = process.env.PUBLIC_URL + '/BloomFavicon.png';

// Warm, context-aware copy per transfer type (an inheritance shouldn't read like a
// cold "Accept transfer"). tail follows a bolded product name.
const HEADINGS = {
    inheritance:  { title: 'You have inherited a piece', tail: 'has been left to you.' },
    gift:         { title: 'A gift for you',           tail: 'is being gifted to you.' },
    resale:       { title: 'Claim your piece',         tail: 'is being transferred to you.' },
    initial_sale: { title: 'Claim your piece',         tail: 'is being transferred to you.' },
};
const headingFor = (t) => HEADINGS[t] || HEADINGS.resale;

// A plain helper (NOT a component) so the auth form's inputs never remount.
const shell = (children) => (
    <div className="auth-page">
        <img className="auth-mark" src={bloomMark} alt="Bloom" />
        {children}
    </div>
);

// Recipient lands here after following a transfer link. Viewing requires login:
// the recipient logs in / registers first, then the authenticated preview loads
// and they can claim (exactly like an initial sale).
const ClaimTransfer = () => {
    const { tokenId } = useParams();
    const navigate = useNavigate();
    // An expired session here must fall back to this page's OWN login form, not a
    // redirect — bouncing them to /user would lose the claim link they followed.
    const [token, setUserToken] = useState(() => {
        const t = getToken('user');
        if (t && isTokenExpired(t)) { clearToken('user'); return null; }
        return t;
    });
    const [info, setInfo] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [claimed, setClaimed] = useState(false);
    const [claimError, setClaimError] = useState(null);
    const [authMode, setAuthMode] = useState('login');
    const [form, setForm] = useState({ name: '', email: '', password: '' });
    const [busy, setBusy] = useState(false);

    const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    // Load the preview only once the viewer is authenticated. The token_id alone
    // no longer grants a preview — the backend requires a user token too.
    useEffect(() => {
        if (!token) return;
        fetch(`${API_BASE_URL}/api/transfer/token/${tokenId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => {
                if (data.error) setLoadError(data.error);
                else setInfo(data);
            })
            .catch(() => setLoadError('Failed to load transfer details'));
    }, [tokenId, token]);

    const handleClaim = async () => {
        setBusy(true);
        setClaimError(null);
        try {
            await apiFetch(`/api/transfer/claim/${tokenId}`, { method: 'POST', token });
            setClaimed(true);
        } catch (err) {
            setClaimError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleAuth = async (e) => {
        e.preventDefault();
        setBusy(true);
        setClaimError(null);
        try {
            const path = authMode === 'login' ? '/api/user/login' : '/api/user/register';
            const body = authMode === 'login'
                ? { email: form.email, password: form.password, platform: clientPlatform() }
                : { ...form, platform: clientPlatform() };
            const data = await apiFetch(path, { method: 'POST', body });
            setToken('user', data.token);
            setUserToken(data.token);   // triggers the authenticated preview fetch
        } catch (err) {
            setClaimError(err.message);
        } finally {
            setBusy(false);
        }
    };

    // ── Not logged in: gate the whole view behind auth ──────────────────────────
    if (!token) return shell(
        <form className="auth-card" onSubmit={handleAuth}>
            <h1 className="auth-title">Claim your piece</h1>
            <p className="auth-sub">
                {authMode === 'login' ? 'Log in' : 'Create an account'} to view and claim it.
            </p>
            {authMode === 'register' && (
                <input className="auth-input" placeholder="Name" value={form.name}
                    onChange={update('name')} />
            )}
            <input className="auth-input" type="email" placeholder="Email"
                value={form.email} onChange={update('email')} required />
            <input className="auth-input" type="password" placeholder="Password"
                value={form.password} onChange={update('password')} required />
            {claimError && <p className="auth-error">{claimError}</p>}
            <button className="auth-submit" type="submit" disabled={busy}>
                {busy ? 'Please wait…' : (authMode === 'login' ? 'Log in' : 'Create account')}
            </button>
            <button type="button" className="auth-toggle"
                onClick={() => { setClaimError(null); setAuthMode(authMode === 'login' ? 'register' : 'login'); }}>
                {authMode === 'login' ? 'Need an account? Sign up' : 'Have an account? Log in'}
            </button>
        </form>
    );

    if (loadError) return shell(
        <div className="auth-card"><p className="auth-error">{loadError}</p></div>
    );

    if (!info) return shell(
        <div className="auth-card"><p className="auth-sub" style={{ margin: 0 }}>Loading…</p></div>
    );

    if (!info.is_valid) return shell(
        <div className="auth-card">
            <h1 className="auth-title">Link unavailable</h1>
            <p className="auth-sub" style={{ margin: 0 }}>This transfer link has already been claimed or has expired.</p>
        </div>
    );

    if (claimed) return shell(
        <div className="auth-card">
            <h1 className="auth-title">It is yours</h1>
            <p className="auth-sub"><strong>{info.product_name}</strong> is now in your collection.</p>
            <button className="auth-submit" onClick={() => navigate('/user/collection')}>View collection</button>
        </div>
    );

    // ── Logged in: preview + claim ──────────────────────────────────────────────
    const heading = headingFor(info.transfer_type);
    return shell(
        <div className="auth-card">
            <h1 className="auth-title">{heading.title}</h1>
            {info.image_url && info.image_url.startsWith('http') && (
                <img className="claim-image" src={info.image_url} alt={info.product_name} />
            )}
            <p className="auth-sub"><strong>{info.product_name}</strong> {heading.tail}</p>
            {claimError && <p className="auth-error">{claimError}</p>}
            <button className="auth-submit" onClick={handleClaim} disabled={busy}>
                {busy ? 'Claiming…' : 'Claim this piece'}
            </button>
        </div>
    );
};

export default ClaimTransfer;
